import React, { useState, useEffect } from "react";
import { useAuth } from "../../../contexts/AuthContext";
import { useUpgrade } from "../../../contexts/UpgradeContext";
import { useNavigation } from "../../../contexts/NavigationContext";
import { useWebSocket } from "../../../contexts/WebSocketContext";
import { formatDate } from "../../../utils/dateUtils";
import { SubscriptionStatusBanner } from "./SubscriptionStatusBanner";
import { PaymentCompletionModal } from "./PaymentCompletionModal";

interface Subscription {
  _id: string;
  plan: "free" | "premium";
  status: "active" | "past_due" | "canceled" | "incomplete" | "trialing";
  totalSeats: number;
  usedSeats: number;
  billingInterval?: "month" | "year";
  limits: {
    maxStaff: number;
    maxProjects: number;
    maxBillExtractionsPerMonth: number;
    maxIntegrations: number;
  };
  currentUsage: {
    staff: number;
    projects: number;
    billExtractionsThisMonth: number;
    integrations: number;
  };
  stripeCurrentPeriodEnd?: string;
}

interface SubscriptionData {
  subscription: Subscription;
  usage: any;
  limits: any;
  remaining: {
    staff: number;
    projects: number;
    billExtractions: number;
    integrations: number;
  };
}

export const SubscriptionManagement: React.FC = () => {
  const { user } = useAuth();
  const { openUpgradeModal } = useUpgrade();
  const { navigate } = useNavigation();
  const { socket } = useWebSocket();
  const [subscriptionData, setSubscriptionData] =
    useState<SubscriptionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pricing, setPricing] = useState<{
    monthly: number;
    annual: number;
  } | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

  const fetchSubscription = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await fetch(`${apiUrl}/api/subscriptions/current`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        setSubscriptionData(data.data);
      } else {
        setError(data.message);
      }
    } catch (err) {
      console.error("Fetch subscription error:", err);
      setError("Failed to load subscription");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscription();
    fetchPricing();
  }, []);

  // Fetch pricing from centralized API
  const fetchPricing = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${apiUrl}/api/stripe/config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success && data.data?.pricing) {
        setPricing({
          monthly: data.data.pricing.monthly || 30,
          annual: data.data.pricing.annual || 300,
        });
      } else {
        setPricing({ monthly: 30, annual: 300 });
      }
    } catch (err) {
      console.error("Failed to fetch pricing:", err);
      setPricing({ monthly: 30, annual: 300 });
    }
  };

  // Listen for real-time subscription updates via Socket.IO
  useEffect(() => {
    if (!socket) return;

    const handleSubscriptionUpdate = (data: {
      plan: "free" | "premium";
      status: string;
      totalSeats: number;
      usedSeats: number;
      stripeCurrentPeriodEnd?: string;
      timestamp: string;
    }) => {
      console.log("📡 Received subscription:updated event:", data);
      // Refresh subscription data
      fetchSubscription();
    };

    socket.on("subscription:updated", handleSubscriptionUpdate);

    return () => {
      socket.off("subscription:updated", handleSubscriptionUpdate);
    };
  }, [socket]);

  const handleDowngrade = async () => {
    if (
      !window.confirm(
        "Are you sure you want to downgrade to the Free plan? You will lose access to unlimited features and all assigned seats.",
      )
    ) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${apiUrl}/api/subscriptions/downgrade`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        alert("Successfully downgraded to Free plan");
        fetchSubscription();
      } else {
        alert(data.message || "Downgrade failed");
      }
    } catch (err) {
      console.error("Downgrade error:", err);
      alert("Failed to downgrade subscription");
    }
  };

  const handleCompletePayment = () => {
    setShowPaymentModal(true);
  };

  const handlePaymentSuccess = () => {
    setShowPaymentModal(false);
    setProcessingPayment(true);
    
    // Wait a moment for webhooks to process, then refresh
    setTimeout(() => {
      fetchSubscription();
      setProcessingPayment(false);
    }, 2000);
  };

  const handlePaymentModalClose = () => {
    setShowPaymentModal(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-lg text-gray-600">Loading subscription...</div>
      </div>
    );
  }

  if (error || !subscriptionData) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-error-800">{error || "Failed to load subscription"}</p>
      </div>
    );
  }

  const { subscription, remaining } = subscriptionData;
  // Premium requires both plan=premium AND status=active (or trialing) - used for feature access
  const isPremium = subscription.plan === "premium" && (subscription.status === "active" || subscription.status === "trialing");
  // Plan display is based on the plan field, not the status
  const isPremiumPlan = subscription.plan === "premium";

  return (
    <div className="space-y-6">
      {/* Payment Status Banner */}
      <SubscriptionStatusBanner
        status={subscription.status as any}
        plan={subscription.plan}
        onCompletePayment={handleCompletePayment}
        onUpdatePaymentMethod={handleCompletePayment}
        loading={processingPayment}
      />

      {/* Current Plan Card */}
      <div className="bg-white dark:bg-gray-700 rounded-lg shadow-md p-6 border border-gray-200 dark:border-gray-600">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {isPremiumPlan ? "Premium Plan" : "Free Plan"}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {isPremiumPlan
                ? `${
                    subscription.billingInterval === "year"
                      ? `$${(pricing?.annual || 300).toFixed(0)} per seat/year`
                      : `$${(pricing?.monthly || 30).toFixed(0)} per seat/month`
                  } - ${subscription.totalSeats} seats`
                : "$0/month"}
            </p>
            {isPremium && subscription.stripeCurrentPeriodEnd && (
              <p className="text-sm text-gray-500 mt-1">
                Renews on {formatDate(subscription.stripeCurrentPeriodEnd)}
              </p>
            )}
          </div>
          <div>
            {isPremiumPlan ? (
              subscription.status === "active" || subscription.status === "trialing" ? (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-200">
                  Active
                </span>
              ) : (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                  {subscription.status === "incomplete" ? "Incomplete" : 
                   subscription.status === "past_due" ? "Past Due" :
                   subscription.status === "canceled" ? "Canceled" :
                   String(subscription.status).charAt(0).toUpperCase() + String(subscription.status).slice(1)}
                </span>
              )
            ) : (
              <button
                onClick={() => openUpgradeModal({})}
                className="px-4 py-2 bg-accent-600 text-white rounded-md hover:bg-accent-700 transition-colors"
              >
                Upgrade to Premium
              </button>
            )}
          </div>
        </div>

        {/* Usage Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Staff Usage */}
          <div className="bg-gray-50 dark:bg-gray-600 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Staff
            </h3>
            <div className="flex items-baseline space-x-2">
              <span className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                {subscription.currentUsage.staff}
              </span>
              <span className="text-gray-500 dark:text-gray-400">
                / {isPremium || subscription.limits.maxStaff === -1 ? "∞" : subscription.limits.maxStaff}
              </span>
            </div>
            {!isPremium && subscription.limits.maxStaff !== -1 && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {remaining.staff} remaining
              </p>
            )}
          </div>

          {/* Projects Usage */}
          <div className="bg-gray-50 dark:bg-gray-600 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Projects
            </h3>
            <div className="flex items-baseline space-x-2">
              <span className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                {subscription.currentUsage.projects}
              </span>
              <span className="text-gray-500 dark:text-gray-400">
                / {isPremium || subscription.limits.maxProjects === -1 ? "∞" : subscription.limits.maxProjects}
              </span>
            </div>
            {!isPremium && subscription.limits.maxProjects !== -1 && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {remaining.projects} remaining
              </p>
            )}
          </div>

          {/* Bill Extractions Usage */}
          <div className="bg-gray-50 dark:bg-gray-600 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Bill Extractions (Month)
            </h3>
            <div className="flex items-baseline space-x-2">
              <span className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                {subscription.currentUsage.billExtractionsThisMonth}
              </span>
              <span className="text-gray-500 dark:text-gray-400">
                /{" "}
                {isPremium || subscription.limits.maxBillExtractionsPerMonth === -1
                  ? "∞"
                  : subscription.limits.maxBillExtractionsPerMonth}
              </span>
            </div>
            {!isPremium && subscription.limits.maxBillExtractionsPerMonth !== -1 && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {remaining.billExtractions} remaining
              </p>
            )}
          </div>

          {/* Integrations Usage */}
          <div className="bg-gray-50 dark:bg-gray-600 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Integrations
            </h3>
            <div className="flex items-baseline space-x-2">
              <span className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                {subscription.currentUsage.integrations || 0}
              </span>
              <span className="text-gray-500 dark:text-gray-400">
                / {isPremium || subscription.limits.maxIntegrations === -1 ? "∞" : subscription.limits.maxIntegrations}
              </span>
            </div>
            {!isPremium && subscription.limits.maxIntegrations !== -1 && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {remaining.integrations} remaining
              </p>
            )}
          </div>
        </div>

        {/* Premium Seats (if premium) */}
        {isPremium && (
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-600">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                  Seats
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {subscription.usedSeats} of {subscription.totalSeats} seats
                  assigned
                </p>
              </div>
              <button
                onClick={() => navigate("/admin?tab=account&subtab=seats")}
                className="px-4 py-2 text-accent-600 dark:text-accent-400 hover:bg-accent-50 dark:hover:bg-gray-600 rounded-md transition-colors"
              >
                Manage Seats
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Plan Comparison */}
      <div className="bg-white dark:bg-gray-700 rounded-lg shadow-md p-6 border border-gray-200 dark:border-gray-600">
        <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
          Plan Features
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Free Plan */}
          <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
              Free
            </h4>
            <ul className="space-y-2">
              <li className="flex items-start">
                <span className="text-success-500 dark:text-success-400 mr-2">
                  ✓
                </span>
                <span className="text-gray-700 dark:text-gray-300">
                  Up to 3 staff members
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-success-500 dark:text-success-400 mr-2">
                  ✓
                </span>
                <span className="text-gray-700 dark:text-gray-300">
                  Up to 3 projects
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-success-500 dark:text-success-400 mr-2">
                  ✓
                </span>
                <span className="text-gray-700 dark:text-gray-300">
                  10 bill extractions/month
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-success-500 dark:text-success-400 mr-2">
                  ✓
                </span>
                <span className="text-gray-700 dark:text-gray-300">
                  2 integrations
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-gray-400 dark:text-gray-600 mr-2">✗</span>
                <span className="text-gray-500 dark:text-gray-500">
                  Unlimited resources
                </span>
              </li>
            </ul>
          </div>

          {/* Premium Plan */}
          <div className="border-2 border-accent-500 dark:border-blue-400 rounded-lg p-4 relative">
            <div className="absolute -top-3 left-4 bg-accent-500 dark:bg-accent-600 text-white px-3 py-1 rounded-full text-sm font-medium">
              Recommended
            </div>
            <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
              Premium
            </h4>
            <div className="mb-3">
              <p className="text-2xl font-bold text-accent-600 dark:text-accent-400">
                ${(pricing?.monthly || 30).toFixed(0)}
                <span className="text-sm font-normal text-gray-600 dark:text-gray-400">
                  /seat/month
                </span>
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                or ${(pricing?.annual || 300).toFixed(0)}/seat/year (save 17%)
              </p>
            </div>
            <ul className="space-y-2">
              <li className="flex items-start">
                <span className="text-success-500 dark:text-success-400 mr-2">
                  ✓
                </span>
                <span className="text-gray-700 dark:text-gray-300">
                  Unlimited staff
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-success-500 dark:text-success-400 mr-2">
                  ✓
                </span>
                <span className="text-gray-700 dark:text-gray-300">
                  Unlimited projects
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-success-500 dark:text-success-400 mr-2">
                  ✓
                </span>
                <span className="text-gray-700 dark:text-gray-300">
                  Unlimited bill extractions
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-success-500 dark:text-success-400 mr-2">
                  ✓
                </span>
                <span className="text-gray-700 dark:text-gray-300">
                  Unlimited integrations
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-success-500 dark:text-success-400 mr-2">
                  ✓
                </span>
                <span className="text-gray-700 dark:text-gray-300">
                  Per-seat user management
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-success-500 dark:text-success-400 mr-2">
                  ✓
                </span>
                <span className="text-gray-700 dark:text-gray-300">
                  Priority support
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Actions */}
      {isPremium && (
        <div className="bg-white dark:bg-gray-700 rounded-lg shadow-md p-6 border border-gray-200 dark:border-gray-600">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
            Manage Subscription
          </h3>
          <div className="flex space-x-4">
            <button
              onClick={handleDowngrade}
              className="px-4 py-2 text-error-600 border border-red-600 rounded-md hover:bg-error-50 transition-colors"
            >
              Downgrade to Free
            </button>
          </div>
        </div>
      )}

      {/* Payment Completion Modal */}
      <PaymentCompletionModal
        isOpen={showPaymentModal}
        onClose={handlePaymentModalClose}
        onSuccess={handlePaymentSuccess}
      />
    </div>
  );
};
